import * as React from 'react';
import type { ViewProps } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  FadeInDown,
  LinearTransition,
  useReducedMotion,
} from 'react-native-reanimated';

import { MOTION } from '@sixam/config/motion';

type MotionPreset = 'fade' | 'rise' | 'none';

const outQuart = Easing.bezier(...MOTION.easingBezier.outQuart);

type MotionViewProps = ViewProps &
  Readonly<{
    children: React.ReactNode;
    className?: string;
    delay?: number;
    layout?: boolean;
    preset?: MotionPreset;
  }>;

function MotionView({
  children,
  delay = 0,
  layout = true,
  preset = 'rise',
  ...props
}: MotionViewProps) {
  const reducedMotion = useReducedMotion();

  const entering = React.useMemo(() => {
    if (preset === 'none') {
      return undefined;
    }

    const duration = reducedMotion ? MOTION.duration.state : MOTION.duration.enter;
    const animation =
      reducedMotion || preset === 'fade'
        ? FadeIn.duration(duration)
        : FadeInDown.duration(duration);

    return animation.easing(outQuart).delay(reducedMotion ? 0 : delay);
  }, [delay, preset, reducedMotion]);

  const layoutTransition = React.useMemo(() => {
    if (!layout || reducedMotion) {
      return undefined;
    }

    return LinearTransition.duration(MOTION.duration.state).easing(outQuart);
  }, [layout, reducedMotion]);

  return (
    <Animated.View entering={entering} layout={layoutTransition} {...props}>
      {children}
    </Animated.View>
  );
}

export { MotionView };
